/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useCallback, useRef, useState, forwardRef } from 'react';
import {
  RefreshCcw,
  Clock,
  Calendar,
  Loader2,
  Ticket,
  DollarSign,
  ChevronRight,
  TrendingUp,
  Package,
  Search,
  Filter,
  XCircle,
  CheckCircle2,
  Truck,
  Timer,
  ChevronDown,
  IndianRupee,
  User,
} from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { useOrders } from '../../contexts/OrderContext';
import { API_URL } from '../../constant/constant';
import axiosInstance from '../../utils/axiosInstance';

// --- Utility & Config ---

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 4,
  }).format(amount);
};

// Vibrant Status Colors
const getStatusConfig = (status) => {
  const s = status?.toLowerCase();
  switch (s) {
    case 'delivered':
    case 'paid':
      return {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircle2,
        border: 'border-green-200',
      };
    case 'outfordelivery':
      return {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        icon: Truck,
        border: 'border-orange-200',
      };
    case 'cancelled':
      return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        icon: XCircle,
        border: 'border-red-200',
      };
    case 'pending':
      return {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        icon: Timer,
        border: 'border-yellow-200',
      };
    default:
      return {
        bg: 'bg-slate-100',
        text: 'text-slate-600',
        icon: Package,
        border: 'border-slate-200',
      };
  }
};

// --- Components ---

// 1. Stats Rail - Compact & Single Row fixed
const StatsRail = ({ stats, loading }) => {
  // Don't render anything until data arrives
  if (loading || !stats.totalOrders) return null;

  const items = [
    {
      label: 'Orders',
      value: stats.totalOrders || 0,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Order Value',
      value: formatCurrency(stats.totalValue || 0),
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
    {
      label: 'Discount',
      value: formatCurrency(stats.totalDiscount || 0),
      color: 'text-red-600',
      bg: 'bg-yellow-50',
    },
  ];

  // Add Tickets only if value > 0

  if (stats.totalTickets > 0) {
    items.splice(4, 0, {
      label: 'Tickets',
      value: stats.totalTickets,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    });
  }

  return (
    <div className="grid grid-cols-4 sm:flex gap-1 overflow-x-auto pb-2 -mx-4 px-2 scrollbar-hide select-none touch-pan-x">
      {items.map((item, idx) => (
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.05 }}
          key={item.label}
          // Decreased padding (p-2.5) and width (min-w-[110px]) to fit more on screen
          className="flex flex-col justify-between min-w-[50px] p-2.5 rounded-lg border border-slate-100 shadow-sm bg-white relative overflow-hidden shrink-0"
        >
          <div
            className={`absolute top-0 left-0 w-full h-0.5 ${item.color.replace('text', 'bg')}`}
          />

          <div className="flex justify-between items-start mb-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">
              {item.label}
            </span>
            {item?.icon && (
              <div className={`p-1 rounded-full ${item.bg}`}>
                <item.icon className={`w-3 h-3 ${item.color}`} />
              </div>
            )}
          </div>
          <p className="text-[12px  ] sm:text-sm font-bold text-slate-800 tracking-tight leading-tight">
            {item.value}
          </p>
        </motion.div>
      ))}
    </div>
  );
};

// 2. Custom Dropdown
const SelectFilter = ({ value, onChange, options, icon: Icon }) => (
  <div className="relative w-full group">
    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
      <Icon className="h-4 w-4 text-orange-500 group-focus-within:text-orange-600 transition-colors" />
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full pl-10 pr-9 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 shadow-sm hover:border-orange-300 transition-all cursor-pointer"
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
      <ChevronDown className="h-4 w-4 text-slate-400 group-focus-within:text-orange-500 transition-colors" />
    </div>
  </div>
);

// 3. Enhanced Order Card - Wrapped in forwardRef to fix the Error
const EnhancedOrderCard = forwardRef(({ order }, ref) => {
  const navigate = useNavigate();
  const statusInfo = getStatusConfig(order.status);
  const StatusIcon = statusInfo.icon;

  const dateObj = new Date(order.orderDate);
  const dateStr = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const timeStr = dateObj.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <motion.div
      ref={ref} // Attach the ref here
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate(`/orders/${order?.id || order?.orderId}`, { state: order })}
      className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)] active:shadow-none transition-all cursor-pointer relative overflow-hidden"
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${statusInfo.bg.replace('bg-', 'bg-')}`}
      />
      <div className="pl-4 pr-3 py-3">
        {/* Header: ID, Price, Status */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                #{order.orderId}
              </span>
              <span className="text-[10px] text-slate-300">•</span>
              <div className="flex items-center gap-1 text-[10px] text-slate-500 font-medium">
                <Calendar className="w-3 h-3 text-orange-400" /> {dateStr}
              </div>
            </div>
            <h3 className="text-lg pt-1 font-extrabold text-slate-900 tracking-tight leading-none">
              {formatCurrency(order.total)}
            </h3>
          </div>

          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-md ${statusInfo.bg} border ${statusInfo.border}`}
          >
            <StatusIcon className={`w-3 h-3 ${statusInfo.text}`} />
            <span className={`text-[10px] font-bold ${statusInfo.text} uppercase`}>
              {order.status}
            </span>
          </div>
        </div>

        {/* Footer: Time & Action */}
        <div className="flex items-center justify-between border-slate-50">
          <div className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
            <Clock className="w-3 h-3 text-slate-400" />
            <span>{timeStr}</span>
          </div>

          <div className="flex items-center gap-1 text-orange-600 text-[11px] font-bold">
            Details <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </motion.div>
  );
});

EnhancedOrderCard.displayName = 'EnhancedOrderCard';

export default function OrderPage() {
  const { orders, loading, loadingMore, hasMore, fetchOrders, loadMoreOrders } = useOrders();
  const { user } = useUser();
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('All Time');

  const [dashboardStats, setDashboardStats] = useState({
    totalOrders: 0,
    totalValue: 0,
    totalTickets: 0,
    totalDiscount: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // --- Filtering Logic ---
  const sortedOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    let filtered = [...orders];

    if (statusFilter !== 'All') {
      filtered = filtered.filter((o) => o.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (dateFilter === 'Today') {
      filtered = filtered.filter((o) => new Date(o.orderDate) >= today);
    } else if (dateFilter === 'Yesterday') {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      const start = new Date(y);
      const end = new Date(today);
      filtered = filtered.filter((o) => {
        const d = new Date(o.orderDate);
        return d >= start && d < end;
      });
    } else if (dateFilter === 'Last 7 Days') {
      const d = new Date(today);
      d.setDate(d.getDate() - 7);
      filtered = filtered.filter((o) => new Date(o.orderDate) >= d);
    }

    return filtered.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));
  }, [orders, statusFilter, dateFilter]);

  // --- Observers & Effects ---
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasMore && !loadingMore && !loading) loadMoreOrders();
    },
    [hasMore, loadingMore, loading, loadMoreOrders]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    observerRef.current = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (element) observerRef.current.observe(element);
    return () => observerRef.current?.disconnect();
  }, [handleObserver]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;
      setStatsLoading(true);
      try {
        const vendorId = user.id;
        const sessionToken = localStorage.getItem('sessionToken') || user?.sessionToken;

        const response = await axiosInstance.get(
          `${API_URL.BASE_ORDER}/rest/admin/dashboard/api/v1/order/per-vendor`,
          {
            headers: {
              accept: 'application/json',
              'X-Vendor-Id': vendorId,
              sessionToken: sessionToken,
              'X-USER-ROLE': 'Vendor',
            },
          }
        );

        const data = response.data || {};
        setDashboardStats({
          totalOrders: data.totalApprovedOrders || 0,
          totalValue: data.totalApprovedAmt || 0,
          totalTickets: data.totalApprovedTickets || 0,
          totalDiscount: data.totalDiscount || 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        // Set to 0 on error
        setDashboardStats({
          totalOrders: 0,
          totalValue: 0,
          totalTickets: 0,
          totalDiscount: 0,
        });
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [user?.id]);

  // --- Main Render ---

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-2">
          {/* Compact Header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-extrabold text-slate-800 tracking-tight">Orders</h1>
            </div>
            <button
              onClick={fetchOrders}
              className="p-2 bg-white border border-orange-100 rounded-full shadow-sm active:scale-95 transition-all"
            >
              <RefreshCcw className={`w-4 h-4 text-orange-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Stats Rail - Compact */}
          <StatsRail stats={dashboardStats} loading={statsLoading} />

          {/* Filters - Sticky */}
          <div className="sticky top-[52px] z-30 bg-[#f8f9fa]/95 backdrop-blur-md py-1 -mx-4 px-4 md:-mx-6 md:px-6 border-b border-slate-100/50 mb-1 shadow-[0_2px_10px_-8px_rgba(0,0,0,0.05)] mt-0">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Filters
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 md:max-w-2xl">
                <SelectFilter
                  icon={Calendar}
                  value={dateFilter}
                  onChange={setDateFilter}
                  options={['All Time', 'Today', 'Yesterday', 'Last 7 Days']}
                />
                <SelectFilter
                  icon={Filter}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    'All',
                    'Pending',
                    'Accepted',
                    'OutForDelivery',
                    'Delivered',
                    'Paid',
                    'Cancelled',
                    'Rescheduled',
                  ]}
                />
              </div>
            </div>
          </div>

          {/* List Content */}
          <div className="space-y-2.5 min-h-[50vh] pb-20 md:pb-4">
            {loading && !orders?.length ? (
              <OrderSkeleton />
            ) : sortedOrders.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                <AnimatePresence mode="popLayout">
                  {sortedOrders.map((order) => (
                    <EnhancedOrderCard key={order.orderId || order.id} order={order} />
                  ))}
                </AnimatePresence>

                <div ref={loadMoreRef} className="py-6 flex justify-center w-full">
                  {loadingMore && <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />}
                  {!hasMore && sortedOrders.length > 5 && (
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      End of list
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex flex-col items-center justify-center py-12 text-center"
  >
    <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-3">
      <Search className="w-6 h-6 text-orange-400" />
    </div>
    <h3 className="text-base font-bold text-slate-900">No orders here</h3>
    <button
      onClick={() => window.location.reload()}
      className="mt-4 text-xs font-bold text-orange-600 uppercase tracking-wide"
    >
      Refresh
    </button>
  </motion.div>
);

const OrderSkeleton = () => (
  <div className="space-y-2.5">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="bg-white rounded-xl border border-slate-100 p-3 h-[90px] animate-pulse relative"
      >
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-100" />
        <div className="pl-4">
          <div className="flex justify-between mb-3">
            <div className="space-y-1.5">
              <div className="h-2.5 bg-slate-100 rounded w-12" />
              <div className="h-5 bg-slate-100 rounded w-20" />
            </div>
            <div className="h-6 bg-slate-100 rounded w-16" />
          </div>
          <div className="h-3 bg-slate-100 rounded w-full" />
        </div>
      </div>
    ))}
  </div>
);
